import {__} from 'js/tool/page/current';
import {isNumeric, parseNumber} from 'js/tool/number';
import {dateParseFormat} from 'js/config/date/formats';
import {getLocaleOptions} from 'js/admin/helpers/spendCap/spendCap';
import dayjs from 'dayjs';
import {REPORT_ACTION_TYPES} from 'js/admin/components/reports/types/enums';
import {ConditionTypes} from 'js/reusable/form/conditions/types';
import {ACTION_EMAIL} from 'js/admin/components/reports/constants';
import {getCurrencyMetaData} from 'js/tool/metaData/helper';
import {formatNumber} from 'js/admin/helpers/grid';
import {FbSpendCapAction} from 'graphql/generated';

// Source: https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address slighlty modified regex
// eslint-disable-next-line max-len
export const EMAIL_REGEX = /^([a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)$/;

export function setTextInput({newValue, values, name}) {
	const value = newValue?.target?.value;

	return {
		...values,
		[name]: {
			...values[name],
			value,
		},
	};
}

export function setBoolInput({values, name}) {
	return {
		...values,
		[name]: {
			...values[name],
			value: !values[name].value,
		},
	};
}

export function setStatusInput({newValue: {value}, values}) {
	const trueValue  = {value: true};
	const falseValue = {value: false};

	if (value === 'active') {
		return {
			...values,
			isActivated: trueValue,
			isDeleted:   falseValue,
		};
	}

	if (value === 'inactive') {
		return {
			...values,
			isActivated: falseValue,
			isDeleted:   falseValue,
		};
	}

	return {
		...values,
		isActivated: falseValue,
		isDeleted:   trueValue,
	};
}

export function setSelectValue({newValue, values, name}) {
	return {
		...values,
		[name]: {
			...values[name],
			value: newValue,
		},
	};
}

export function setObjectValue({newValue, values, name}) {
	return {
		...values,
		[name]: {
			...values[name],
			value: newValue,
		},
	};
}

export function setMultipleInputsValue({newValue, values, name}) {
	return {
		...values,
		[name]: {
			...values[name],
			value: newValue,
		},
	};
}

export function setExternalAdAccountIds({newValue, values, name}) {
	if (!newValue) {
		return {
			...values,
			[name]: {
				...values[name],
				value: [],
			},
		};
	}

	return {
		...values,
		[name]: {
			...values[name],
			value: newValue.map(acc => acc.value),
		},
	};
}

export function setParentAgencyInfo({newValue, values}) {
	return {
		...values,
		parentAgencyId:         {value: [newValue.value]},
		externalAdvertiserId:   {value: [newValue.value]},
		externalAdvertiserName: {value: newValue.label},
	};
}

export function setImage({newValue, values, name}) {
	return {
		...values,
		[name]: {
			...values[name],
			value: newValue},
	};
}

export function setBusinessManagerId({newValue: {target: {value}}, values, name}) {
	const businessId = value.split('business_id=')[1];

	if (businessId) {
		return {
			...values,
			[name]: {
				...values[name],
				value: businessId},
		};
	}

	const assetId = value.split('asset_id=')[1];

	if (assetId) {
		return {
			...values,
			[name]: {
				...values[name],
				value: assetId.split('&')[0]},
		};
	}

	return setTextInput({newValue: {target: {value}}, values, name});
}

export function onlyTrueValidator({formEntry, name}) {
	if (!formEntry.value) {
		return {[name]: __('fb:has to be checked')};
	}

	return null;
}

export function repValueValidator({formEntry, name, values}) {
	if (!formEntry.value && values.representative.value.value === 'NO') {
		return {[name]: __('validator:field required')};
	}
}

export function numberValidator({formEntry, name}) {
	if (!isNumeric(formEntry.value)) {
		return {[name]: __('fb:only numerc values')};
	}
}

export function zohoExchangeRateValidator({formEntry, name}) {
	if (formEntry.value !== '' && !isNumeric(formEntry.value)) {
		return {[name]: __('fb:only numerc values')};
	}

	return null;
}

export function validateBusinessManagerId({formEntry, name}) {
	if (formEntry.value) {
		const businessId = formEntry.value.split('business_id=')[1];

		if (!isNaN(businessId)) {
			return null;
		}

		if (businessId) {
			return {[name]: __('fb:invalid business id url')};
		}
	}

	return numberValidator({formEntry, name});
}

export function oneOfValidator(otherValue) {
	return function ({values, formEntry, name}) {
		if (validateRequired(values[otherValue])) {
			return null;
		}

		return !validateRequired(formEntry) ?
		    {[name]: __('fb:entry is required')} :
			null
		;
	};
}

export function requiredWhenValidator(otherValue) {
	return function ({values, formEntry, name}) {
		if (values[otherValue].value && !validateRequired(formEntry)) {
			return {[name]: __('fb:entry is required')};
		}

		return null;
	};
}

export function passwordValidator({values}) {
	if (values.password.value !== values.repeatPassword.value) {
		return {
			password:       __('fb:pass dont match'),
			repeatPassword: __('fb:pass dont match'),
		};
	}

	return null;
}

export function validateEmptyArray({values, name}) {
	if (!values[name].value || values[name].value.length === 0) {
		return {[name]: __('fb:entry is required')};
	}

	return null;
}

export function spendCapValidator({values, formEntry, name}) {
	if (values.transactionMode.value.value === FbSpendCapAction.RemoveAll) {
		return null;
	}

	if (!validateRequired(formEntry)) {
		return {[name]: __('fb:entry is required')};
	}

	return isValidNumberInput({...formEntry});
}

export function validateRequired(formEntry) {
	let value = formEntry.value;

	if (value && value.trim) {
		value = value.trim();
	}

	return !!value || value === false || (value && Object.keys(value).length > 0);
}

export function adminCredentialsValidator({values}) {
	if (
		!values.system.value &&
		!values.users.value &&
		!values.finance.value &&
		!values.fb_ad_accounts.value &&
		!values.reports.value &&
		!values.stats.value
	) {
		return {'credentials': __('fb:atleast one credential required')};
	}

	return null;
}

export function bigQueryFieldValidator({values, name}) {
	if (
		values.action.value &&
		values.action.value.value !== REPORT_ACTION_TYPES.BIG_QUERY
	) {
		return null;
	}

	if (!values[name].value) {
		return {[name]: __('fb:entry is required')};
	}

	if (!(/^[a-zA-Z_$][a-zA-Z_$0-9]*$/.test(values[name].value))) {
		return {[name]: __('fb:bigquery invalid name')};
	}

	return null;
}

export function reportEmailInputsValidator(fieldValidator) {
	return function ({values, name, isFromChange, ignoreDidChangeValidation}) {
		if (values.action.value && values.action.value.value !== ACTION_EMAIL) {
			return null;
		}

		return multiInputsValidator(fieldValidator)({values, name, isFromChange, ignoreDidChangeValidation});
	};
}

export function multiInputsValidator(fieldValidator, allowDuplicates = false) {
	return function({values, name, isFromChange, ignoreDidChangeValidation}) {
		const multiInputs    = values[name].value;
		const inputsRequired = values[name].required !== false;

		const validationErrors = {[name]: multiInputs.reduce((errors, input, idx) => {
			if (isFromChange && !input.change && !ignoreDidChangeValidation) {
				return errors;
			}

			if ((input.value === '' || (input && input.value === '')) && inputsRequired) {
				return {
					...errors,
					[idx.toString()]: __('fb:entry is required'),
				};
			}

			if (
				!allowDuplicates &&
				multiInputs.filter(
					tmpInput => tmpInput.value === input.value
				).length > 1
			) {
				return {
					...errors,
					[idx.toString()]: __('fb:duplicates not allowed'),
				};
			}

			const fieldValidatorError = fieldValidator(input.value);

			if (fieldValidatorError) {
				return {
					...errors,
					[idx.toString()]: fieldValidatorError,
				};
			}

			return errors;
		}, {})};

		return Object.keys(validationErrors[name]).length === 0 ? null : validationErrors;
	};
}

export function emailValidator({formEntry, name}) {
	if (formEntry.value === '') {
		return null;
	}

	return EMAIL_REGEX.test(formEntry.value) && formEntry.value.length < 255 ? null : {[name]: __('fb:invalid email')};
}

export function emailValueValidator(email) {
	return (EMAIL_REGEX.test(email) && email.length < 255) ? null : __('fb:invalid email');
}

export function emailOrEmptyValueValidator(email) {
	return (EMAIL_REGEX.test(email) && email.length < 255) || email === '' ? null : __('fb:invalid email');
}

export function agreementValidator({formEntry, name, values}) {
	if (!!formEntry.value || values.action.value.value !== ACTION_EMAIL) {
		return null;
	}

	return {[name]: __('validator:field must accept')};
}

export function notEmptyArrayValidator({formEntry, name}) {
	if (formEntry.value.length === 0) {
		return {[name]: __('fb:at least one entry required')};
	}

	return null;
}

export function conditionsValidator({formEntry, name}) {
	const errors = new Array(formEntry.value.length).fill(null);

	for (let i = 0; i < formEntry.value.length; i++) {
		const condition = formEntry.value[i];
		const type = condition.type;

		if (condition.operator === ConditionTypes.OPERATOR_IS_NULL) {
			continue;
		}

		if (
			type === ConditionTypes.DATE &&
			(condition.value.trim() === '' || dayjs(condition.value, dateParseFormat, true).isValid())
		) {
			continue;
		}

		if (type === ConditionTypes.BOOL && condition.value) {
			continue;
		}

		if (condition.value && condition.value.trim() === '') {
			errors[i] = __('fb:entry is required');

			continue;
		}

		if (type === ConditionTypes.CURRENCY && !isNumeric(condition.value)) {
			errors[i] = __('fb:invalid value use decimal sep', {'DEC_SEP': getLocaleOptions().decimalPoint});
		}
	}

	if (errors.find(v => v !== null)) {
		return {[name]: errors};
	}

	return null;
}

export function sameCurrencyValidator({formEntry, name}) {
	if (
		formEntry.value.reduce((accu, fbAcc) => {
			if (!accu) {
				return fbAcc.currency;
			}

			return accu === fbAcc.currency ? accu : 'MIX';
		}, null) === 'MIX'
	) {
		return {[name]: __('fb:no mixed currency accounts')};
	}

	return null;
}

export function customerAdAccountsCurrencyValidator(currency)
{
	return function({formEntry, name}) {
		const reducedCurrency = formEntry.value.reduce((accu, fbAcc) => {
			if (!accu) {
				return fbAcc.currency;
			}

			return accu === fbAcc.currency ? accu : 'MIX';
		}, null);

		if (
			reducedCurrency === 'MIX'
		) {
			return {[name]: __('fb:no mixed currency accounts')};
		}

		if (
			currency && reducedCurrency && reducedCurrency !== currency
		) {
			return {[name]: __('fb:wrong customer currency', {CURRENCY: currency})};
		}

		return null;
	};
}

export function numberInputValidator({formEntry, name}) {
	const isInvalid = isValidNumberInput(formEntry);

	if (isInvalid) {
		return {[name]: isInvalid};
	}

	return null;
}

export function getDecimalPlaces(value) {
	return (typeof value === 'string' ? value : Number(value) + '').split?.('.')?.[1]?.length || 0;
}

export function isValidNumberInput(
	{value, required, currency, min, max, maxValidationMsg, hideErrorWhenEmpty, disabled, validationError}
) {
	const currencyMetadata = getCurrencyMetaData(currency);

	if (disabled || value === 0) {
		return false;
	}

	if (hideErrorWhenEmpty && !value) {
		return false;
	}

	if (validationError) {
		return validationError;
	}

	if (currency && currencyMetadata.decimalPlaces === 0 && getDecimalPlaces(value)) {
		return __('fb:currency no decimals');
	}

	if (getDecimalPlaces(value) > getCurrencyMetaData(currency ?? '').decimalPlaces) {
		return __(
			'fb:invalid value use decimal sep',
			{
				DEC_SEP: getLocaleOptions().decimalPoint,
				ZEROS:   currency ? Array(currencyMetadata.decimalPlaces).fill('0').join('') : '00',
			}
		);
	}

	if (value === '' && required) {
		return __('fb:entry is required');
	}

	if (
		typeof parseNumber(value) === 'number' && (min !== null && parseNumber(value) < Number(min))
	) {
		return __('fb:value to small');
	} else if (typeof max === 'number' && parseNumber(value) && parseNumber(value) > Number(max)) {
		if (maxValidationMsg) {
			return __(maxValidationMsg) + ': ' +
			formatNumber(max,
				getLocaleOptions(currency),
				'currency');
		}

		return __('fb:value too big');
	}

	return null;
}

export function isValidDecimalCount(value, currency) {
	const currencyMetadata = getCurrencyMetaData(currency);

	return getDecimalPlaces(value) <= currencyMetadata.decimalPlaces;
}

